import { formatUtcDate, isValidDateStr } from '../utils/usageTimelineDisplay';

export interface UsageHistoryBounds { min: string; max: string }
export type StatsRangeMode = 'today' | 'week' | 'month' | 'custom';
export const USAGE_DASHBOARD_SOURCE_HISTORY_DAYS = 90;

// File-profile fallback; GCP controls use the server's advertised history_bounds.
export function usageDashboardDateBounds(now = new Date()): UsageHistoryBounds {
  const max = now.toISOString().slice(0, 10);
  const earliest = new Date(`${max}T00:00:00Z`);
  earliest.setUTCDate(earliest.getUTCDate() - USAGE_DASHBOARD_SOURCE_HISTORY_DAYS);
  return { min: formatUtcDate(earliest), max };
}

export function parseUsageHistoryBounds(value: unknown): UsageHistoryBounds | null {
  const bounds = value as UsageHistoryBounds | undefined;
  return bounds && isValidDateStr(bounds.min) && isValidDateStr(bounds.max)
    && bounds.min <= bounds.max ? { min: bounds.min, max: bounds.max } : null;
}

export function statsPresetWindow(
  mode: Exclude<StatsRangeMode, 'custom'>, utcDay: string,
  bounds: UsageHistoryBounds | null,
) {
  const end = bounds?.max ?? utcDay;
  const start = new Date(`${end}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - (mode === 'today' ? 0 : mode === 'week' ? 7 : 30));
  const day = formatUtcDate(start);
  return { start: bounds && day < bounds.min ? bounds.min : day, end };
}

export function validateUsageDateRange(
  start: string, end: string, bounds: UsageHistoryBounds | null,
): { ok: boolean; message?: string } {
  if (!bounds || !isValidDateStr(start) || !isValidDateStr(end)) return { ok: false };
  // Ignore incomplete year edits from native date inputs.
  const currentYear = new Date().getUTCFullYear();
  if ([start, end].some(day => Number(day.slice(0, 4)) < 2000
    || Number(day.slice(0, 4)) > currentYear + 1)) return { ok: false };
  if (end < start) return { ok: false, message: 'End date must be on or after start date.' };
  if (start < bounds.min || end < bounds.min || start > bounds.max || end > bounds.max) {
    return { ok: false, message: `Choose dates from ${bounds.min} through ${bounds.max}.` };
  }
  if ((Date.parse(end) - Date.parse(start)) / 86400000 > 30) {
    return { ok: false, message: 'Please choose a range of 30 days or fewer.' };
  }
  return { ok: true };
}
